import { Cta } from "@/components/Cta";
import { MediaPlaceholder } from "@/components/MediaPlaceholder";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { TerminalCard } from "@/components/TerminalCard";
import { useSeo } from "@/lib/seo";
import {
  Card,
  CodeBlock,
  Container,
  Grid,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@repo/ui/core";
import {
  IconBrandGitlab,
  IconCheck,
  IconTestPipe,
  IconUsers,
} from "@repo/ui/icons";
import styles from "@styles/site.module.css";

const features = [
  {
    title: "Behavioral Testing",
    description:
      "Test Android builds, REST APIs, and full system behavior with readable Gherkin specifications.",
    icon: IconTestPipe,
  },
  {
    title: "40+ Languages",
    description:
      "Product owners write requirements in any of 40+ supported Gherkin languages. No code required.",
    icon: IconUsers,
  },
  {
    title: "CI Integration",
    description:
      "Tests execute automatically in any GitLab CI pipeline stage with detailed merge request reporting.",
    icon: IconBrandGitlab,
  },
];

const gherkinPoints = [
  "Reuse fixtures across languages",
  "Integrated with GitLab CI and merge requests",
  "Execute tests in any environment",
];

const ciPoints = [
  "Automatic test execution on every commit",
  "Rich test reports in merge requests",
  "Parallel test execution for faster feedback",
  "Detailed failure diagnostics and logs",
];

export default function Testing() {
  useSeo("/products/testing");

  return (
    <>
      <PageHero
        eyebrow="Automated Testing"
        title={
          <>
            Test requirements,{" "}
            <span className={styles.gradientText}>not just code</span>
          </>
        }
        description="Product owners write requirements in plain Gherkin. CI executes them against live emulators on every merge request, with results reported back in GitLab."
      >
        <Cta to="/contact">Book a Demo</Cta>
      </PageHero>

      {/* Feature cards */}
      <Section>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          {features.map((feat, i) => (
            <Reveal key={feat.title} delay={i * 0.05}>
              <Card
                padding="xl"
                className={styles.card}
                style={{ height: "100%" }}
              >
                <Stack gap="md">
                  <span className={styles.iconChip}>
                    <feat.icon size={20} stroke={1.5} />
                  </span>
                  <Title order={3} size="1.125rem" fw={600}>
                    {feat.title}
                  </Title>
                  <Text c="dimmed" size="sm" lh={1.7}>
                    {feat.description}
                  </Text>
                </Stack>
              </Card>
            </Reveal>
          ))}
        </SimpleGrid>
      </Section>

      {/* Readable specs */}
      <Section hairline>
        <Grid gutter={64} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <SectionHeader
              title="Readable specifications, automated validation"
              description="Write tests in plain language that both product owners and engineers understand. Reuse fixtures across languages and environments."
            />
            <Reveal>
              <ul className={styles.checkList}>
                {gherkinPoints.map((point) => (
                  <li key={point}>
                    <IconCheck size={16} stroke={2.5} />
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Reveal delay={0.08} y={24}>
              <TerminalCard title="features/display.feature">
                <CodeBlock
                  code={`# language: en
Feature: Display and Graphics
  Background:
    Given adb is connected to "$EMULATOR"
    And the device is booted

  Scenario: Screen is on and unlocked
    When I check the screen state
    Then the screen should be on
    And the device should be unlocked

  Scenario: Capture screenshot
    When I execute "adb shell screencap"
    Then the file should exist on device`}
                  language="gherkin"
                  withCopyButton={false}
                  style={{ fontSize: "0.8125rem", background: "transparent" }}
                  styles={{
                    pre: {
                      background: "transparent",
                      padding: 0,
                      margin: 0,
                      lineHeight: 1.8,
                    },
                  }}
                />
              </TerminalCard>
            </Reveal>
          </Grid.Col>
        </Grid>
      </Section>

      {/* CI integration */}
      <Section hairline>
        <Grid gutter={64} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Reveal y={24}>
              <div className={styles.mediaFrame}>
                <MediaPlaceholder
                  label="GitLab CI test report with gherkin scenario results per requirement"
                  ratio="16 / 10"
                  src="/null/screenshot.ci.test.png"
                />
                <div className={styles.frameCaption}>
                  GitLab CI / test report
                </div>
              </div>
            </Reveal>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <SectionHeader
              title="CI integration with GitLab"
              description="Tests execute automatically in GitLab CI pipelines with detailed reporting and merge request integration. See exactly what passed, what failed, and why."
            />
            <Reveal>
              <ul className={styles.checkList}>
                {ciPoints.map((point) => (
                  <li key={point}>
                    <IconCheck size={16} stroke={2.5} />
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
          </Grid.Col>
        </Grid>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title
              order={2}
              fw={650}
              size="2rem"
              style={{ letterSpacing: "-0.02em" }}
            >
              Requirements your CI can actually verify
            </Title>
            <Text
              size="lg"
              c="dimmed"
              maw={480}
              mx="auto"
              mt="md"
              mb="xl"
              lh={1.65}
            >
              Bring a requirement, watch it run against a live emulator.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
